'use client';
import { useInView } from 'framer-motion';
import {
  FC,
  MouseEvent,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { delay } from 'utils/delay';
import Accordion from './Accordion';
import Entering from './Entering';

type TAcordion = {
  title?: ReactNode;
  children?: ReactNode;
  defaultOpen?: boolean;
};

interface IAcordions {
  acordions?: TAcordion[];
}

const Accordions: FC<PropsWithChildren<IAcordions>> = ({ acordions }) => {
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const refDiv = useRef<HTMLDivElement | null>(null);
  const openedRef = useRef<HTMLButtonElement | null>(null);
  const inView = useInView(refDiv, {
    once: true,
    amount: 'all'
  });

  const [tryToOpen, setTryToOpen] = useState(true);

  const handleClick = (index: number) => async (e: MouseEvent<HTMLButtonElement>) => {
    if (!!buttonRefs.current?.length) {
      const clickedButton = buttonRefs.current[index];

      if (clickedButton === openedRef.current) return (openedRef.current = null);

      if (openedRef.current && !!openedRef.current?.getAttribute('data-value')) {
        openedRef.current?.click();

        e.preventDefault();
        e.stopPropagation();

        await delay(450);
        clickedButton?.click();
      }

      openedRef.current = clickedButton;
    }
  };

  const getRef = (index: number) => (ref: HTMLButtonElement | null) =>
    (buttonRefs.current[index] = ref);

  const handler = () => {
    if (typeof window !== 'undefined') {
      const elements = window?.document?.querySelectorAll(
        'div[aria-expanded="false"]'
      ) as NodeListOf<HTMLDivElement>;

      if (!!elements.length) elements[0]?.click();
    }
  };

  const handlerTimer = useCallback(async () => {
    await delay(200);
    handler();
    await delay(200);
    handler();
    if (buttonRefs.current) openedRef.current = buttonRefs.current[0];
    setTryToOpen(false);
  }, []);

  useEffect(() => {
    if (tryToOpen && inView) handlerTimer();
  }, [handlerTimer, inView, tryToOpen]);

  return (
    <div ref={refDiv}>
      {acordions?.map(({ children, title }, index) => (
        <Entering key={'acordion' + index} initial={false}>
          <Accordion title={title} onClick={handleClick(index)} ref={getRef(index)}>
            {children}
          </Accordion>
        </Entering>
      ))}
    </div>
  );
};

export default Accordions;
